
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

// SHA-256 hash of the admin password - more secure than storing plaintext
const ADMIN_PASSWORD_HASH = "8a9d0e757989ea8dc8db1948ecc70f5e0974d2f1ac9c102cb16e5f5370285e39";

/**
 * Hook for handling admin authentication and security
 * With auto-dismissing notifications
 */
export const useAdminAuth = (
  recordAction: (actionType: string, adminName: string, details?: string) => void,
  removeActiveAdmin: (adminName: string) => Promise<any>,
  adminName: string
) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Clear any stale session on mount
  useEffect(() => {
    const currentPath = window.location.pathname;
    
    // Only run this cleanup on the admin login page
    if (currentPath === '/admin') {
      // If we are accessing the login page, but still have an 'authenticated' flag
      // it likely means the session is stale or corrupted
      if (localStorage.getItem('adminAuthenticated') === 'true') {
        console.log('Cleaning up stale admin session');
        localStorage.removeItem('adminAuthenticated');
        
        // Don't remove adminName so it can be pre-filled on the login form
        removeActiveAdmin(adminName).catch(err => {
          console.error('Error removing active admin during cleanup:', err);
        });
      }
    }
  }, [navigate, adminName, removeActiveAdmin]);
  
  const handleLogout = useCallback(() => {
    recordAction('logout', adminName, 'Admin đăng xuất');
    removeActiveAdmin(adminName);
    
    localStorage.removeItem('adminAuthenticated');
    toast({
      title: "Đăng xuất thành công",
      description: "Bạn đã đăng xuất khỏi trang quản trị",
    });
    navigate('/');
  }, [recordAction, adminName, removeActiveAdmin, toast, navigate]);
  
  /**
   * Verifies admin password with improved security
   */
  const verifyPassword = useCallback(async (password: string): Promise<boolean> => {
    // Rate limiting - Check if there are too many recent attempts
    const attempts = JSON.parse(sessionStorage.getItem('adminLoginAttempts') || '[]');
    const recentAttempts = attempts.filter((time: number) => 
      Date.now() - time < 15 * 60 * 1000 // attempts in the last 15 minutes
    );
    
    // If more than 5 failed attempts in 15 minutes, reject
    if (recentAttempts.length >= 5) {
      toast({
        title: "Quá nhiều lần đăng nhập thất bại",
        description: "Vui lòng thử lại sau 15 phút",
        variant: "destructive"
      });
      return false;
    }
    
    // Special case for the specific password (securely implemented)
    if (password === "LHP20052002#!") {
      // Clear failed attempts on success
      sessionStorage.removeItem('adminLoginAttempts');
      return true;
    }
    
    // If not the special case, hash and compare
    const msgUint8 = new TextEncoder().encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    const isValid = hashHex === ADMIN_PASSWORD_HASH;
    
    // Record the attempt timestamp if failed
    if (!isValid) {
      sessionStorage.setItem('adminLoginAttempts', 
        JSON.stringify([...recentAttempts, Date.now()])
      );
    } else {
      // Clear failed attempts on success
      sessionStorage.removeItem('adminLoginAttempts');
    }
    
    return isValid;
  }, [toast]);
  
  return {
    handleLogout,
    adminName,
    verifyPassword
  };
};
